










































































































































































































































































































































































































































































































































import Vue from 'vue';
import moment from 'moment';
import { AxiosError } from 'axios';
import { defineComponent, PropType } from '@vue/composition-api';

import {
  WorkflowFormFieldTypeEnum,
  IWorkflowFormFieldData,
  IWorkflowFormFieldOptionSubmissionData,
  IWorkflowFormFieldSubmissionData,
  IWorkflowFormSectionData,
  IWorkflowFormSectionFieldData,
  IWorkflowFormSectionSubmissionData,
  IWorkflowFormSectionSubmissionFieldErrorData,
  WorkflowConstraintActionEnum,
  WorkflowFormFieldSubmission,
  IWorkflowFormFieldOptionData,
  WorkflowFormFieldSubmissionAttachment,
  IAttachmentData,
  IWorkflowFormField,
  IWorkflowFormSectionFieldConstraintData,
  WorkflowFormSectionFieldConstraint
} from '@/models';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';

import {
  convertDataURlToFile,
  createFile,
  downloadDataUrl,
  isValidDataUrl,
  setErrorFromAxiosError
} from '@/utils/helpers';
import { numericValidator } from '@/utils/validators';

import AlSelect from '../common/AlSelect.vue';
import DatePicker from '../common/DatePicker.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';
import WorkflowFormSubmissionCalculatedField from './WorkflowFormSubmissionCalculatedField.vue';

export interface IFieldAttributes {
  disabled: boolean;
  required: boolean;
  readonly: boolean;
}

export default defineComponent({
  name: 'WorkflowFormSubmissionField',

  mixins: [mixins(auth, snackbar)],

  components: {
    AlSelect,
    DatePicker,
    DragDropFileInput,
    WorkflowFormSubmissionCalculatedField
  },

  props: {
    section_field: {
      type: Object as PropType<IWorkflowFormSectionFieldData>,
      required: true
    },
    sectionIndex: {
      type: Number,
      required: true
    },
    fieldIndex: {
      type: Number,
      required: true
    },
    templateIndex: {
      type: Number,
      required: false
    },
    answers: {
      type: Array as PropType<IWorkflowFormSectionSubmissionData[]>,
      required: true
    },
    fieldError: {
      type: [String, Array] as PropType<string | string[]>,
      required: false
    },
    isReadonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      WorkflowFormFieldTypeEnum,
      WorkflowConstraintActionEnum
    };
  },

  computed: {
    fieldTemplate(): IWorkflowFormField {
      return this.section_field.field_template as IWorkflowFormField;
    }
  },

  methods: {
    isValidDataUrl,
    numericValidator,

    numericFieldValidator(v: string): boolean | string {
      if (this.isReadonly) {
        return true;
      }

      // Allow empty string for optional fields
      if (v === '') {
        return true;
      }

      // Validate non-empty values
      return this.numericValidator(v);
    },

    clearAllErrors(): void {
      this.$emit('clearAllErrors');
    },

    isConstraintSatisfied(
      constraint: IWorkflowFormSectionFieldConstraintData,
      fieldSubmissions: IWorkflowFormSectionSubmissionData[],
      templateIndex?: number
    ) {
      const constraintInstance = new WorkflowFormSectionFieldConstraint(
        constraint
      );

      const answers = fieldSubmissions
        .flatMap(ans => ans.fields!)
        .map(answer => new WorkflowFormFieldSubmission(answer));

      // Find if any constraint is satisfied
      return constraintInstance.isSatisfied(answers, templateIndex);
    },

    genWorkflowFormFieldProperties(
      sectionField: IWorkflowFormSectionFieldData,
      fieldIndex = -1
    ): IFieldAttributes {
      let actionAttributes = {
        disabled: sectionField.disabled,
        required: sectionField.required,
        readonly: this.isReadonly
      };

      if (sectionField.constraints.length > 0) {
        //goes through list of constraints
        sectionField.constraints.forEach(constraint => {
          //find if all constraint’s target option is selected
          const constraintConditionsMet = this.isConstraintSatisfied(
            constraint,
            this.answers,
            this.templateIndex
          );

          if (constraintConditionsMet) {
            switch (constraint.action) {
              case WorkflowConstraintActionEnum.Required:
                actionAttributes.disabled = false;
                actionAttributes.required = true;

                break;

              case WorkflowConstraintActionEnum.Optional:
              case WorkflowConstraintActionEnum.Hidden:
                actionAttributes.disabled = false;
                actionAttributes.required = false;

                break;

              case WorkflowConstraintActionEnum.Disabled:
                actionAttributes.disabled = true;
                actionAttributes.required = false;

                break;

              default:
                break;
            }
          }
        });
      }

      const sectionFieldAnswer = this.answers[this.sectionIndex].fields[
        fieldIndex
      ];

      // For template child rows we avoid auto-clearing values when a constraint disables a sibling,
      // because each row should retain its own value history.
      this.updateDisabledFieldAnswer(
        actionAttributes.disabled,
        sectionFieldAnswer,
        fieldIndex
      );

      return actionAttributes;
    },

    updateDisabledFieldAnswer(
      isDisabled: boolean,
      sectionFieldAnswer: IWorkflowFormFieldSubmissionData,
      fieldIndex = -1
    ) {
      if (
        isDisabled &&
        fieldIndex != -1 &&
        (sectionFieldAnswer.value ||
          sectionFieldAnswer.option_submissions.length > 0)
      ) {
        //clears submission answer when disabled
        const submission = new WorkflowFormFieldSubmission({
          field: sectionFieldAnswer.field,
          id: sectionFieldAnswer.id,
          value: '',
          option_submissions: []
        });

        this.answers[this.sectionIndex].fields.splice(
          fieldIndex,
          1,
          submission
        );
      }
    },

    getFieldError(
      fieldErrors: IWorkflowFormSectionSubmissionFieldErrorData[],
      sectionIndex: number,
      errorFieldIndex: number
    ): string {
      return fieldErrors[sectionIndex]
        ? fieldErrors[sectionIndex].errors[errorFieldIndex]
        : '';
    },

    genOptionItems(
      field: IWorkflowFormFieldData
    ): IWorkflowFormFieldOptionData[] {
      return field.options!.map(data => {
        return { ...data, option: data.id };
      });
    },

    genRadioBtnOptionsValue(
      radio: IWorkflowFormFieldOptionData,
      submissions: IWorkflowFormFieldOptionSubmissionData[]
    ): IWorkflowFormFieldOptionSubmissionData {
      let value = submissions.find(submission => submission.option == radio.id);

      if (!value) {
        value = {
          id: undefined,
          option: radio.id
        } as IWorkflowFormFieldOptionSubmissionData;
      }

      return value;
    },

    genInputId(section_field: IWorkflowFormSectionFieldData) {
      return `input-${section_field.id}`;
    },

    genLabel(section_field: IWorkflowFormSectionFieldData) {
      const attributes = this.genWorkflowFormFieldProperties(section_field);
      let label = (section_field.field_template as IWorkflowFormFieldData)
        .label;
      if (attributes.required) {
        label = `${label}*`;
      }
      return label;
    },

    downloadFileAttachment(
      base64: string,
      section_field: IWorkflowFormSectionFieldData
    ) {
      downloadDataUrl(
        base64,
        (section_field.field_template as IWorkflowFormFieldData).label
      );
    },

    genFileFieldValue(sectionIndex: number, fieldIndex: number) {
      return this.answers[sectionIndex].fields[fieldIndex].value
        ? convertDataURlToFile(
            this.answers[sectionIndex].fields[fieldIndex].value,
            'Attachment'
          )
        : null;
    },

    genMultiFileFieldValue(sectionIndex: number, fieldIndex: number) {
      return this.answers[sectionIndex].fields[fieldIndex].attachments
        ?.filter(attachment => attachment.file instanceof File)
        .map(attachment => attachment.file);
    },

    async addWorkflowFormFieldAttachments(
      files: File[],
      sectionIndex: number,
      fieldIndex: number
    ) {
      this.clearAllErrors();

      const newAttachments = files.map(
        file => new WorkflowFormFieldSubmissionAttachment({ file })
      );

      const existingAttachments =
        this.answers[sectionIndex].fields[fieldIndex].attachments ?? [];

      Vue.set(this.answers[sectionIndex].fields[fieldIndex], 'attachments', [
        ...existingAttachments,
        ...newAttachments
      ]);
    },

    async downloadFileFromAttachment(attachment: IAttachmentData) {
      try {
        const data =
          typeof attachment.file === 'string'
            ? await this.$services.fileLoader.downloadFile(
                attachment.file as string
              )
            : attachment.file!;
        const fileName = attachment.filename || 'Attachment';
        createFile(data, fileName);

        (this as any).createSnack({
          color: 'success darken-1',
          type: 'success',
          right: true,
          useBtn: false,
          msg: `File attachment download successfully`,
          btnText: 'Ok',
          timeout: 6000
        });
      } catch (err) {
        const error = setErrorFromAxiosError(err as AxiosError);
        (this as any).createSnack({
          color: 'error darken-1',
          type: 'error',
          right: true,
          useBtn: false,
          msg: `File attachment download failed: ${error.message}`,
          btnText: 'Ok',
          timeout: 6000
        });
      }
    },

    removeFileFromAttachment(
      sectionIndex: number,
      fieldIndex: number,
      attachment: IAttachmentData
    ) {
      const attachments =
        this.answers[sectionIndex].fields[fieldIndex].attachments ?? [];

      const indexOfAttachment = attachments.indexOf(attachment);

      attachments.splice(indexOfAttachment, 1);

      Vue.set(
        this.answers[sectionIndex].fields[fieldIndex],
        'attachments',
        attachments
      );
    },

    async addWorkflowFormAttachment(
      file: File | null,
      sectionIndex: number,
      fieldIndex: number
    ) {
      this.clearAllErrors();
      let base64 = '';

      if (file) {
        base64 = await this.getBase64(file);
      }
      Vue.set(this.answers[sectionIndex].fields[fieldIndex], 'value', base64);
    },

    getBase64(file: File) {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result?.toString() || '');
        reader.onerror = error => reject(error);
      });
    },

    onDateFieldInput(
      date: string,
      sectionIndex: number,
      fieldIndex: number,
      urgencyThreshold: number
    ) {
      this.clearAllErrors();

      this.answers[sectionIndex].fields[fieldIndex].value = date;

      if (urgencyThreshold > 0) {
        let isDateInThreshold = false;

        if (date) {
          const dateValue = moment(date);

          isDateInThreshold = dateValue.isBetween(
            moment(new Date()).startOf('day'),
            moment(new Date())
              .add(urgencyThreshold, 'days')
              .endOf('day'),
            'hours',
            '[]'
          );
        }

        Vue.set(
          this.answers[sectionIndex].fields[fieldIndex],
          'is_urgent',
          isDateInThreshold
        );
      }
    }
  }
});
